// suiteAppAccessService.js
//
// Build 25 backfill: existing users (created before the assignment system
// existed) don't have an `assignedSuiteApps` field. Under the new default-DENY
// policy, they'd suddenly see zero dashboard cards on first login.
//
// To make rollout invisible, we backfill every existing user with the full
// list of active suite apps. Admins don't need this (they bypass the check),
// but we backfill them anyway for consistency.
//
// The backfill runs once on Admin login. It's idempotent via a marker on a
// system doc — if `system/suiteAppsBackfill.completed === true`, skip.
//
// Why on Admin login (vs Cloud Function)? Same pattern as the Build 23 defect
// number backfill — keeps everything client-side and avoids needing a Cloud
// Function deploy. Risk: an admin must log in for the backfill to fire. In
// practice that happens within minutes of any deploy.

import {
  collection, doc, getDoc, getDocs, writeBatch, setDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAllActiveAppIds } from '../constants/appCatalog';

const SYSTEM_DOC = 'system/suiteAppsBackfill';

/**
 * Backfill suite-app access for all users who don't have an
 * `assignedSuiteApps` field. Grants them the full active app list.
 *
 * Idempotent: subsequent calls do nothing once the marker is set.
 * Safe to call from React's useEffect.
 */
export async function backfillSuiteAppAccess() {
  // Check marker first so we don't even fetch users on subsequent runs.
  const markerRef = doc(db, SYSTEM_DOC);
  try {
    const markerSnap = await getDoc(markerRef);
    if (markerSnap.exists() && markerSnap.data().completed === true) {
      return { skipped: true };
    }
  } catch (err) {
    // If we can't even read the marker, we probably can't write to /system/
    // either — bail quietly.
    console.warn('Suite-apps backfill: marker read failed, skipping', err);
    return { skipped: true, error: err };
  }

  const allAppIds = getAllActiveAppIds();
  const usersSnap = await getDocs(collection(db, 'users'));

  const toUpdate = [];
  usersSnap.docs.forEach(d => {
    const data = d.data();
    // Only backfill if the field is genuinely missing. If an admin has
    // already restricted someone (set assignedSuiteApps to []), we respect
    // that explicit choice and leave it alone.
    if (data.assignedSuiteApps === undefined) {
      toUpdate.push(d.id);
    }
  });

  if (toUpdate.length === 0) {
    // Nothing to do; still set the marker so we don't re-scan.
    await setDoc(markerRef, {
      completed: true,
      completedAt: serverTimestamp(),
      usersUpdated: 0
    }, { merge: true });
    return { skipped: false, usersUpdated: 0 };
  }

  // Batch up updates. Firestore batch limit is 500 ops; chunk if needed.
  const CHUNK = 400;
  let processed = 0;
  for (let i = 0; i < toUpdate.length; i += CHUNK) {
    const chunk = toUpdate.slice(i, i + CHUNK);
    const batch = writeBatch(db);
    chunk.forEach(uid => {
      batch.update(doc(db, 'users', uid), { assignedSuiteApps: allAppIds });
    });
    await batch.commit();
    processed += chunk.length;
  }

  await setDoc(markerRef, {
    completed: true,
    completedAt: serverTimestamp(),
    usersUpdated: processed
  }, { merge: true });

  console.log(`Suite-apps backfill: granted access to ${processed} user(s)`);
  return { skipped: false, usersUpdated: processed };
}
